import Sortable from 'sortablejs';
import { baseSortableOptions } from 'helpers/sortable';
import ApplicationController from './application_controller';

export default class extends ApplicationController {
  static targets = [
    'checkbox',
    'saveButton',
    'form',
    'scopeRadio',
    'scopeLabel',
    'orgResetButton',
    'userResetButton',
    'visibleContainer',
    'hiddenContainer',
    'visibleCount',
    'hiddenCount',
    'visibleEmpty',
    'hiddenEmpty',
    'columnItem',
  ];

  static values = {
    orgUrl: String,
    userUrl: String,
    orgPersisted: Boolean,
    userPersisted: Boolean,
    orgColumns: Array,
    userColumns: Array,
    defaultColumns: Array,
  };

  connect() {
    // Initialize sortable when modal is shown (element is visible)
    this.boundOnModalShown = this.onModalShown.bind(this);
    $(this.element).on('shown.bs.modal', this.boundOnModalShown);
  }

  disconnect() {
    this.sortable?.destroy();
    this.sortable = null;
    $(this.element).off('shown.bs.modal', this.boundOnModalShown);
  }

  onModalShown() {
    this.initializeSortable();
  }

  initializeSortable() {
    if (!this.hasVisibleContainerTarget) return;

    if (this.sortable) {
      this.sortable.destroy();
    }

    this.sortable = new Sortable(this.visibleContainerTarget, {
      ...baseSortableOptions,
      handle: '.drag-handle',
      animation: 150,
      onEnd: () => this.updateColumnOrder(),
    });
  }

  updateColumnOrder() {
    // Get order from visible container only (exclude empty state)
    const visibleItems = this.getVisibleColumnItems();
    const orderedChecked = visibleItems.map((item) => item.dataset.columnKey);

    const scope = this.getCurrentScope();
    if (scope === 'organization') {
      this.orgColumnsValue = orderedChecked;
    } else {
      this.userColumnsValue = orderedChecked;
    }
  }

  getCurrentScope() {
    if (!this.hasScopeRadioTarget) return 'organization';
    const checked = this.scopeRadioTargets.find((r) => r.checked);
    return checked ? checked.value : 'organization';
  }

  // Get column items in visible container (excludes empty state)
  getVisibleColumnItems() {
    return this.columnItemTargets.filter((item) => this.visibleContainerTarget.contains(item));
  }

  // Get column items in hidden container (excludes empty state)
  getHiddenColumnItems() {
    return this.columnItemTargets.filter((item) => this.hiddenContainerTarget.contains(item));
  }

  toggleColumn(event) {
    const checkbox = event.target;
    const { columnKey } = checkbox.dataset;
    const columnItem = this.columnItemTargets.find((item) => item.dataset.columnKey === columnKey);

    if (!columnItem) return;

    if (checkbox.checked) {
      this.moveToVisible(columnItem);
    } else {
      this.moveToHidden(columnItem);
    }

    this.updateCounts();
    this.updateSaveButtonState();
    this.updateColumnOrder();
  }

  moveToVisible(item) {
    // Add drag handle if not present
    const placeholder = item.querySelector('.drag-handle-placeholder');
    if (placeholder) {
      const dragHandle = document.createElement('span');
      dragHandle.className = 'drag-handle mr-2 text-muted';
      dragHandle.title = 'Drag to reorder';
      dragHandle.innerHTML = '<i class="bi bi-grip-vertical"></i>';
      placeholder.replaceWith(dragHandle);
    }

    item.dataset.draggable = 'true';

    // Insert before the empty state element
    const emptyState = this.visibleContainerTarget.querySelector('.column-empty-state');
    if (emptyState) {
      this.visibleContainerTarget.insertBefore(item, emptyState);
    } else {
      this.visibleContainerTarget.appendChild(item);
    }
  }

  moveToHidden(item) {
    // Remove drag handle, add placeholder
    const dragHandle = item.querySelector('.drag-handle');
    if (dragHandle) {
      const placeholder = document.createElement('span');
      placeholder.className = 'drag-handle-placeholder mr-2';
      placeholder.style.width = '16px';
      dragHandle.replaceWith(placeholder);
    }

    item.dataset.draggable = 'false';

    // Insert before the empty state element
    const emptyState = this.hiddenContainerTarget.querySelector('.column-empty-state');
    if (emptyState) {
      this.hiddenContainerTarget.insertBefore(item, emptyState);
    } else {
      this.hiddenContainerTarget.appendChild(item);
    }
  }

  updateCounts() {
    const visibleCount = this.getVisibleColumnItems().length;
    const hiddenCount = this.getHiddenColumnItems().length;

    if (this.hasVisibleCountTarget) {
      this.visibleCountTarget.textContent = visibleCount;
    }
    if (this.hasHiddenCountTarget) {
      this.hiddenCountTarget.textContent = hiddenCount;
    }

    // Toggle empty states
    if (this.hasVisibleEmptyTarget) {
      this.visibleEmptyTarget.classList.toggle('d-none', visibleCount > 0);
    }
    if (this.hasHiddenEmptyTarget) {
      this.hiddenEmptyTarget.classList.toggle('d-none', hiddenCount > 0);
    }
  }

  updateSaveButtonState() {
    if (!this.hasSaveButtonTarget) return;

    const visibleCount = this.getVisibleColumnItems().length;
    this.saveButtonTarget.disabled = visibleCount === 0;
  }

  switchScope(event) {
    const scope = event.target.value;
    const isOrgScope = scope === 'organization';

    // Update form action and method
    if (this.hasFormTarget) {
      const url = isOrgScope ? this.orgUrlValue : this.userUrlValue;
      const isPersisted = isOrgScope ? this.orgPersistedValue : this.userPersistedValue;

      this.formTarget.action = url;
      this.formTarget.method = 'post';

      let methodInput = this.formTarget.querySelector('input[name="_method"]');
      if (isPersisted) {
        if (!methodInput) {
          methodInput = document.createElement('input');
          methodInput.type = 'hidden';
          methodInput.name = '_method';
          this.formTarget.appendChild(methodInput);
        }
        methodInput.value = 'patch';
      } else if (methodInput) {
        methodInput.remove();
      }
    }

    // Update UI for the selected scope
    this.updateCheckboxesForScope(scope);

    // Toggle reset buttons
    if (this.hasOrgResetButtonTarget) {
      this.orgResetButtonTarget.style.display = isOrgScope ? '' : 'none';
    }
    if (this.hasUserResetButtonTarget) {
      this.userResetButtonTarget.style.display = isOrgScope ? 'none' : '';
    }

    // Update active state on labels
    this.scopeLabelTargets.forEach((label) => {
      const radio = label.querySelector('input[type="radio"]');
      if (radio && radio.value === scope) {
        label.classList.add('active');
        label.classList.remove('btn-outline-dark');
        label.classList.add('btn-primary');
      } else {
        label.classList.remove('active');
        label.classList.remove('btn-primary');
        label.classList.add('btn-outline-dark');
      }
    });
  }

  updateCheckboxesForScope(scope) {
    const isOrgScope = scope === 'organization';
    const columnsToShow = isOrgScope ? this.orgColumnsValue : this.userColumnsValue;

    // Move all column items to appropriate containers based on scope preference
    this.columnItemTargets.forEach((item) => {
      const { columnKey } = item.dataset;
      const checkbox = item.querySelector('input[type="checkbox"]');
      const shouldBeVisible = columnsToShow.includes(columnKey);

      // Update checkbox state
      if (checkbox) {
        checkbox.checked = shouldBeVisible;
      }

      // Move to appropriate container
      if (shouldBeVisible) {
        this.moveToVisible(item);
      } else {
        this.moveToHidden(item);
      }
    });

    // Reorder visible items to match saved preference order
    this.reorderVisibleColumns(columnsToShow);

    this.updateCounts();
    this.updateSaveButtonState();
  }

  reorderVisibleColumns(orderedKeys) {
    if (!this.hasVisibleContainerTarget) return;

    const container = this.visibleContainerTarget;
    const emptyState = container.querySelector('.column-empty-state');
    const items = this.getVisibleColumnItems();
    const itemMap = new Map(items.map((item) => [item.dataset.columnKey, item]));

    // Reorder based on saved preference - insert before empty state
    orderedKeys.forEach((key) => {
      const item = itemMap.get(key);
      if (item) {
        if (emptyState) {
          container.insertBefore(item, emptyState);
        } else {
          container.appendChild(item);
        }
      }
    });
  }
}
